import axios from 'axios'
import { ElMessage } from 'element-plus'
import Cookies from 'js-cookie'

/**
 * 从当前 URL 路径中解析租户编码
 * URL 格式如: /zero-carbon/gallery → tenantCode = "zero-carbon"
 * 如果无法解析，返回空字符串
 */
export function getTenantCode() {
  const path = window.location.hash ? window.location.hash.replace('#', '') : window.location.pathname
  const match = path.match(/^\/([a-zA-Z0-9_-]+)/)
  return (match && match[1]) ? match[1] : ''
}

/**
 * 获取租户 API 前缀
 * /api/{tenantCode}/portal 或回退到 /portal
 */
export function getTenantPrefix() {
  return '/portal'
}

/** 管理端登录后写入 Token（B端认证） */
export function setAdminToken(token) {
  Cookies.set('Admin-Token', token)
}

/** 管理端登出 */
export function removeAdminToken() {
  Cookies.remove('Admin-Token')
}

/** 判断当前页面是否为 B 端管理页面 */
function isAdminPage() {
  const hash = window.location.hash.replace('#', '')
  return hash.includes('/admin')
}

// 创建axios实例
// baseURL 从环境变量读取：开发环境为空（走 vue-cli proxy），生产环境为 /api（走 nginx）
const service = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '',
  timeout: 30000
})

// 不需要携带token的匿名接口列表
const anonymousUrls = [
  '/portal/work/list',
  '/portal/work/detail',
  '/portal/activity',
  '/portal/auth',
  '/portal/registration',
  '/portal/file'
]

// 文件上传路径取消超时限制（含 STS 凭证获取 + 文件上传）
const uploadUrlPatterns = ['/portal/file/upload', '/common/upload', '/portal/oss/sts-token', '/tenant-admin/oss/sts-token']

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 文件上传不设超时限制（大文件上传可能需要几分钟）
    if (uploadUrlPatterns.some(url => config.url.includes(url))) {
      config.timeout = 0
    }

    // ✅ 增强调试：打印请求信息（生产环境可关闭）
    if (process.env.NODE_ENV !== 'production') {
      const isAnonymous = anonymousUrls.some(url => config.url.includes(url))
      if (process.env.NODE_ENV !== 'production') console.log('[Request] URL:', config.baseURL + config.url, '| isAnonymous:', isAnonymous)
    }

    // 从 URL hash 中提取租户编码，通过 Header 传给后端
    const tenantCode = getTenantCode()
    if (tenantCode) {
      config.headers['X-Tenant-Code'] = tenantCode
    }

    // C端：token 所属租户与当前 URL 租户不一致时，清除 token 并跳转登录
    if (!isAdminPage() && tenantCode) {
      const cTenant = localStorage.getItem('c_tenant') || ''
      if (cTenant && cTenant !== tenantCode) {
        localStorage.removeItem('c_token')
        localStorage.removeItem('c_tenant')
        // 阻止本次请求，跳转登录页
        window.location.hash = '#/' + tenantCode + '/login'
        return Promise.reject(new Error('租户已切换，请重新登录'))
      }
    }

    // 按页面上下文选择 Token，不跨端 fallback，避免 B/C 端 token 互相干扰
    let token
    if (isAdminPage()) {
      token = localStorage.getItem('tenant_token') || Cookies.get('Admin-Token')
      // 校验 tenant_token 格式（JWT 应包含两个点）
      if (token && token.split('.').length !== 3) {
        console.warn('[Auth] 无效的 tenant_token 格式，清除')
        localStorage.removeItem('tenant_token')
        token = null
      }
    } else {
      token = localStorage.getItem('c_token')
      if (token && token.split('.').length !== 3) {
        if (process.env.NODE_ENV !== 'production') console.warn('[Auth] 无效的 c_token 格式')
        localStorage.removeItem('c_token'); token = null
      }
    }

    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token
    }
    
    return config
  },
  error => {
    console.log(error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data

    // 如果是 blob/arraybuffer 响应（如文件下载），直接返回
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return res
    }
    
    // ✅ 增强调试：打印响应信息（生产环境可关闭）
    if (process.env.NODE_ENV !== 'production') {
      if (process.env.NODE_ENV !== 'production') console.log('[Response] URL:', response.config.url, '| Code:', res.code)
    }
    
    // 如果返回的不是JSON格式，直接返回
    if (typeof res !== 'object') {
      return res
    }
    
    // 若依框架返回格式: { code: 200, msg: "xxx", data: {} }
    if (res.code !== 200) {
      // ✅ 过滤敏感错误信息，不暴露技术细节
      let errorMsg = res.msg || '请求失败'
      
      // 如果是数据库异常、SQL异常等，显示友好提示
      if (errorMsg.includes('SQL') || 
          errorMsg.includes('sql') ||
          errorMsg.includes('Database') ||
          errorMsg.includes('database') ||
          errorMsg.includes('Exception') ||
          errorMsg.includes('exception') ||
          errorMsg.includes('Error updating database')) {
        errorMsg = '数据操作失败，请稍后重试'
      }
      
      ElMessage({
        message: errorMsg,
        type: 'error',
        duration: 5 * 1000
      })
      
      // 未登录或登录过期（code=401 或 msg 包含"未登录"）
      if (res.code === 401 || (res.msg && res.msg.includes('未登录'))) {
        const tc = getTenantCode()
        if (isAdminPage()) {
          localStorage.removeItem('tenant_token')
          if (tc) window.location.hash = `#/${tc}/admin/login`
        } else {
          localStorage.removeItem('c_token')
          if (tc) window.location.hash = `#/${tc}`
        }
        Cookies.remove('Admin-Token')
        return Promise.reject(new Error('登录已过期'))
      }
      
      return Promise.reject(new Error(errorMsg))
    } else {
      return res
    }
  },
  error => {
    console.log('err', error)
    
    // ✅ 增强：打印详细错误信息
    if (process.env.NODE_ENV !== 'production') {
      console.error('[Response] 请求失败:', {
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      })
    }
    
    // ✅ 过滤敏感错误信息
    let errorMsg = error.message || '请求失败'
    
    // 如果是网络错误或服务器错误，显示友好提示
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      errorMsg = '请求超时，请稍后重试'
    } else if (error.response && error.response.status >= 500) {
      errorMsg = '服务器繁忙，请稍后重试'
    } else if (error.response && error.response.status === 404) {
      errorMsg = '请求的资源不存在'
    } else if (error.response && error.response.status === 401) {
      errorMsg = '认证失败，请重新登录'
      const tc = getTenantCode()
      if (isAdminPage()) {
        localStorage.removeItem('tenant_token')
        if (tc) setTimeout(() => { window.location.hash = `#/${tc}/admin/login` }, 500)
      } else {
        localStorage.removeItem('c_token')
        if (tc) setTimeout(() => { window.location.hash = `#/${tc}` }, 500)
      }
      Cookies.remove('Admin-Token')
    }

    ElMessage({
      message: errorMsg,
      type: 'error',
      duration: 5 * 1000
    })
    return Promise.reject(error)
  }
)

export default service
